/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { IShimmerStyleProps, IShimmerStyles, Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CopyButton } from 'src/core/components/CopyButton';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';

type ResourceKeyCellProps = { account: AzureCsAccountStore; forceDisplayCopyButton?: boolean; ariaLabel?: string };

const messages = defineMessages({
    key: { id: 'portal.resourceKeyCell.key', defaultMessage: 'Key' },
    viewLabel: { id: 'portal.resourceKeyCell.viewLabel', defaultMessage: 'view key button' },
    hideLabel: { id: 'portal.resourceKeyCell.hideLabel', defaultMessage: 'hide key button' }
});

const theme = getScopedTheme('ResourceKeyCell');
const shimmerStyles = classNamesFunction<IShimmerStyleProps, IShimmerStyles>()({
    root: { width: '50%' },
    shimmerGradient: {
        backgroundImage: `linear-gradient(to right, ${theme.shimmerEdgeColor} 0%, ${theme.shimmerMiddleColor} 50%, ${theme.shimmerEdgeColor} 100%)`
    }
});

export const ResourceKeyCell = observer((props: ResourceKeyCellProps) => {
    const { account, forceDisplayCopyButton = false, ariaLabel = '' } = props;
    const tracker = useProgressTracker();
    const { dispatcher, rootStore } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const [hiddenKey, setHiddenKey] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const { canListResourceKeys } = mapUserRoleToUserActions(userRole);

    const { localize } = useLocalization();

    const toggleKeyState = () => {
        if (!account.keyStrings.key1) {
            dispatcher.dispatch('fetchAndSetCsAccountKeyByPath', { tracker, path: account.path, csAccountStore: account });
        }
        setHiddenKey(!hiddenKey);
    };

    return (
        <Stack horizontal={isLargeOrGreater} verticalAlign="center">
            {tracker.isRunning ? (
                <Shimmer styles={shimmerStyles} shimmerElements={[{ type: ShimmerElementType.line, height: 20 }]} />
            ) : (
                <LuisText styles={{ root: { overflowWrap: 'break-word' } }}>
                    {hiddenKey ? '*'.repeat(32) : account.keyStrings.key1}
                </LuisText>
            )}
            <Stack horizontal grow verticalAlign="center">
                <RBACTooltip
                    hidden={canListResourceKeys}
                    displayedUserRole={displayedUserRole}
                    componentSurroundedByToolTip={
                        <IconButton
                            onClick={toggleKeyState}
                            disabled={!canListResourceKeys}
                            styles={{ root: { background: 'transparent' } }}
                            iconProps={{ iconName: hiddenKey ? 'View' : 'Hide3' }}
                            aria-label={ariaLabel + localize(hiddenKey ? messages.viewLabel : messages.hideLabel)}
                        />
                    }
                />
                {(forceDisplayCopyButton || !hiddenKey) && (
                    <CopyButton textToCopy={account.keyStrings.key1} label={localize(messages.key)} />
                )}
            </Stack>
        </Stack>
    );
});
